import React from 'react';

import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { isDate } from 'lodash';
import { DateTime } from 'luxon';
import { formatCurrency } from '@cnx-saas/utils-fe';
import { UserLanguages } from '@cnx-saas/common';
import { ShipmentStop, TransportOrderDocumentProps } from '../types';
import { calculateTotalCost } from '../utils/shipmentCostCalculation';
import { Draft, DraftEng } from './Draft';
import { CancelWatermark, CancelWatermarkEng } from './CancelWatermark';

const toDateTime = (date: Date | string, timezone: string) => {
  if (isDate(date)) {
    return DateTime.fromJSDate(date, { zone: timezone });
  }
  return DateTime.fromISO(date);
};

const formatDate = (date: Date, timezone: string) => {
  if (date) {
    return toDateTime(date, timezone).toFormat('dd.MM.yyyy');
  } else {
    return '';
  }
};

type LoadingUnloadingTextProps = {
  loading: string;
  unloading: string;
  numberOfPickUps: number;
  numberOfDropOffs: number;
  counts: {
    pickUp: number;
    dropOff: number;
  };
};

const loadingUnloadingText = (
  stop: ShipmentStop,
  {
    loading,
    unloading,
    numberOfPickUps,
    numberOfDropOffs,
    counts,
  }: LoadingUnloadingTextProps,
) => {
  const isPickUpOnly = stop.pickUp.length > 0 && stop.dropOff.length === 0;
  const isDropOff = stop.dropOff.length > 0;

  if (isPickUpOnly) {
    const postfix = numberOfPickUps > 1 ? `${counts.pickUp + 1}` : '';
    counts.pickUp++;
    return `${loading} ${postfix}:`.trim();
  }

  if (isDropOff) {
    const postfix = numberOfDropOffs > 1 ? `${counts.dropOff + 1}` : '';
    counts.dropOff++;
    return `${unloading} ${postfix}:`.trim();
  }

  return '';
};

const formatTimeWindow = (date: Date, timezone: string) =>
  toDateTime(date, timezone).toFormat('HH:mm');

const languageMapperContent = {
  [UserLanguages.DE]: {
    title1: 'Transportauftrag',
    title2: 'Transportanfrage',
    subtitle: 'Wie vereinbart, übernehmen Sie folgenden Transport:',
    subtitle2: 'Anfrage für folgenden Transport:',
    date: 'Datum:',
    stop: 'Stop',
    loadingTime: 'Ladezeit/Entladezeit:',
    loads: 'Ladungen:',
    reference: 'Ladereferenz',
    prodecure: 'Vorgang',
    pallets: 'Paletten',
    loadingMeter: 'Lademeter',
    content: 'Inhalt',
    weight: 'Gewicht',
    notes: 'Anmerkungen',
    loading: 'Beladung',
    unloading: 'Entladung',
    freightPrice: 'Frachtpreis',
    equipment: 'Equipment',
    legal: ` ** Die Übermittlung von Transportanfragen oder Transportaufträgen
          durch die Cargonexx GmbH stellt keine vertragliche Grundlage oder
          Verpflichtung dar, sondern dient ausschließlich der
          Informationsübermittlung. Ein rechtsverbindlicher Vertragsabschluss
          erfolgt erst durch die direkte Unterzeichnung oder die ausdrückliche
          Zustimmung zu einem Vertragsverhältnis zwischen dem Auftraggeber
          (Versender/Empfänger) und dem Spediteur (Frachtführer).`,
  },
  [UserLanguages.GB]: {
    title1: 'Transport order',
    title2: 'Transportation request',
    subtitle: 'As agreed, you will provide the following transportation:',
    subtitle2: 'Request for the following transportation',
    date: 'Date:',
    stop: 'Stop',
    loadingTime: 'Loading time/Unloading time:',
    loads: 'Loads:',
    reference: 'Loading reference',
    prodecure: 'Process',
    pallets: 'Pallets',
    loadingMeter: 'Loading meter',
    content: 'Content',
    weight: 'Weight',
    notes: 'Notes',
    loading: 'Loading',
    unloading: 'Unloading',
    freightPrice: 'Freight price',
    equipment: 'Equipment',
    legal: ` ** The transmission of transport requests or transport orders by Cargonexx GmbH does not constitute a contractual basis or obligation, but serves solely to provide information. A legally binding contract is only concluded by the direct signature or the express consent to a contractual relationship between the client (shipper/consignee) and the freight forwarder (carrier).`,
  },
};

export const TransportOrderDocument = ({
  assignment,
  shipment,
  shipper,
  isDraft,
  logoUrl,
  isCanceled,
}: TransportOrderDocumentProps) => {
  const content =
    languageMapperContent?.[
      assignment.preferredLanguage === UserLanguages.DE ||
      !assignment.preferredLanguage
        ? UserLanguages.DE
        : UserLanguages.GB
    ];

  const numberOfPickUps = shipment.stops.filter(
    (stop) => stop.pickUp.length > 0,
  ).length;
  const numberOfDropOffs = shipment.stops.filter(
    (stop) => stop.dropOff.length > 0,
  ).length;

  const counts = { pickUp: 0, dropOff: 0 };

  const watermark = isDraft ? (
    <View
      fixed
      style={{
        position: 'absolute',
        top: '50',
        left: '40px',
        transform: 'scale(1.4)',
        zIndex: '-1',
      }}
    >
      {assignment.preferredLanguage === UserLanguages.DE ? (
        <Draft />
      ) : (
        <DraftEng />
      )}
    </View>
  ) : (
    ''
  );
  const watermarkCanceled = isCanceled ? (
    <View
      fixed
      style={{
        position: 'absolute',
        top: '50',
        left: '40px',
        transform: 'scale(1.4)',
        zIndex: '-1',
      }}
    >
      {assignment.preferredLanguage === UserLanguages.DE ? (
        <CancelWatermark />
      ) : (
        <CancelWatermarkEng />
      )}
    </View>
  ) : (
    ''
  );
  const titleText = !isDraft ? content.title1 : content.title2;
  const subtitleText = !isDraft ? content.subtitle : content.subtitle2;

  const vehicleBodyType = shipment.vehicle?.vehicleType;

  const totalCost =
    assignment.rate?.agreedTotalCost ??
    assignment.rate?.calculatedTotalCost ??
    calculateTotalCost(shipment, assignment.rate);

  const requirements = shipment.stops.flatMap(
    (stop) => stop.location.loadingRequirements,
  );
  const uniqueRequirements = Array.from(new Set(requirements));

  return (
    <Document>
      <Page
        size="A4"
        style={{
          padding: '35px 35px 75px 35px',
          fontSize: '10px',
          fontFamily: 'Helvetica',
        }}
      >
        {watermark}
        {watermarkCanceled}
        <View
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <View>
            <View style={{ paddingBottom: '10px' }}>
              <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: '12px' }}>
                {shipper.name}
              </Text>
              <Text>{`${shipper.address?.street}, ${shipper.address?.zipCode} ${shipper.address?.city}, ${shipper.address?.country}`}</Text>
            </View>
            <Text style={{ fontFamily: 'Helvetica-Bold' }}>
              {assignment?.carrier.name}
            </Text>
            <Text>{assignment?.carrier.address?.street ?? ''}</Text>
            <Text>
              {`${assignment?.carrier.address?.country ?? ''} ${
                assignment?.carrier.address?.zipCode ?? ''
              } ${assignment?.carrier.address?.city ?? ''}`}
            </Text>
          </View>
          <View style={{ display: 'flex', justifyContent: 'space-between' }}>
            <View
              style={{
                alignSelf: 'flex-end',
                width: '100%',
                height: '60pt',
                overflow: 'hidden',
                display: 'flex',
                maxWidth: '150pt',
                justifyContent: 'flex-end',
              }}
            >
              {logoUrl && (
                <Image
                  style={{
                    width: 'auto',
                    height: '100%',
                    objectFit: 'contain',
                    marginLeft: 'auto',
                  }}
                  src={logoUrl}
                />
              )}
            </View>
            <View
              style={{
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <Text>{shipper.contact?.name}</Text>
              <Text>{shipper.contact?.email}</Text>
            </View>
          </View>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: '20px',
            marginBottom: '20px',
            width: '100%',
          }}
        >
          <Text
            style={{ fontFamily: 'Helvetica-Bold', fontSize: '12px', flex: 1 }}
          >
            {titleText}: {shipment.referenceId}
          </Text>
          <Text style={{ flex: 1, textAlign: 'right' }}>
            {DateTime.now().toFormat('dd.MM.yyyy')}
          </Text>
        </View>
        <View
          style={{
            width: '100%',
          }}
        >
          <Text>{subtitleText}</Text>
        </View>
        {vehicleBodyType && (
          <View
            style={{
              fontFamily: 'Helvetica-Bold',
              fontSize: '12px',
              marginTop: '20px',
              width: '100%',
            }}
          >
            <Text>{vehicleBodyType}</Text>
          </View>
        )}
        <View
          style={{
            borderTop: '2px solid #000',
            marginTop: '15px',
            marginBottom: '15px',
            height: '1px',
            width: '100%',
          }}
        ></View>
        <View>
          {shipment.stops.map((stop, index) => (
            <View
              key={index}
              style={
                index === 0
                  ? {
                      borderBottom: '2px dashed #000',
                      paddingBottom: '15px',
                    }
                  : {
                      borderBottom: '2px dashed #000',
                      paddingTop: '15px',
                      paddingBottom: '15px',
                    }
              }
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingLeft: '5px',
                }}
              >
                <View style={{ width: '30%' }}>
                  <Text
                    style={{ fontFamily: 'Helvetica-Bold', fontSize: '12px' }}
                  >
                    {`${loadingUnloadingText(stop, {
                      loading: content.loading,
                      unloading: content.unloading,
                      numberOfPickUps,
                      numberOfDropOffs,
                      counts,
                    })}`}
                  </Text>
                </View>
                <View style={{ width: '70%' }}>
                  <Text>{stop.location.company}</Text>
                  <Text>{`${stop.location.address.street} ${
                    stop.location.address.houseNumber ?? ''
                  }`}</Text>
                  <Text>{`${stop.location.address.country} - ${stop.location.address.zipCode} ${stop.location.address.city}`}</Text>
                  {(stop.location.contact?.firstName ||
                    stop.location.contact?.lastName) && (
                    <Text>{`${stop.location.contact?.firstName ?? ''} ${
                      stop.location.contact?.lastName ?? ''
                    } ${stop.location.contact?.phone ?? ''} ${
                      stop.location.contact?.email ?? ''
                    }`}</Text>
                  )}
                  <Text>
                    {stop.location.openingHours?.openingHoursType === 'CUSTOM'
                      ? 'Jeden Tag (Mon-Son)'
                      : 'Jeden Wochentag'}
                  </Text>
                </View>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingLeft: '5px',
                  paddingTop: '10px',
                }}
              >
                <View style={{ width: '30%' }}>
                  <Text>{content.date}</Text>
                </View>
                <View style={{ width: '70%' }}>
                  <Text>
                    {stop.plannedTimes &&
                      stop.plannedTimes.plannedArrivalTime &&
                      formatDate(
                        stop.plannedTimes.plannedArrivalTime,
                        stop.location.openingHours.timezone,
                      )}
                  </Text>
                </View>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingLeft: '5px',
                }}
              >
                <View style={{ width: '30%' }}>
                  <Text>{content.loadingTime}</Text>
                </View>
                <View style={{ width: '70%' }}>
                  <Text style={{ width: '200px' }}>
                    {stop.plannedTimes &&
                      stop.plannedTimes.plannedArrivalTime &&
                      stop.plannedTimes.plannedDepartureTime &&
                      `${formatTimeWindow(
                        stop.plannedTimes.plannedArrivalTime,
                        stop.location.openingHours.timezone,
                      )} - ${formatTimeWindow(
                        stop.plannedTimes.plannedDepartureTime,
                        stop.location.openingHours.timezone,
                      )}`}
                  </Text>
                </View>
              </View>
              <View style={{ width: '100%' }}>
                <Text
                  style={{
                    fontFamily: 'Helvetica-Bold',
                    fontSize: '12px',
                    paddingTop: '10px',
                    paddingLeft: '5px',
                  }}
                >
                  {content.loads}
                </Text>
              </View>
              <View style={{ width: '100%', marginTop: '10px' }}>
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    border: '1px solid #000',
                  }}
                >
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      borderRight: '1px solid #000',
                      display: 'flex',
                      width: '20%',
                      padding: '4px',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>
                      {content.reference}
                    </Text>
                  </View>
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      borderRight: '1px solid #000',
                      padding: '4px',
                      width: '15%',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>
                      {content.prodecure}
                    </Text>
                  </View>
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      borderRight: '1px solid #000',
                      padding: '4px',
                      width: '15%',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>
                      {content.pallets}
                    </Text>
                  </View>
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      borderRight: '1px solid #000',
                      padding: '4px',
                      width: '15%',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>
                      {content.loadingMeter}
                    </Text>
                  </View>
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      borderRight: '1px solid #000',
                      padding: '4px',
                      width: '15%',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>
                      {`${content.weight} (kg)`}
                    </Text>
                  </View>
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      borderRight: '1px solid #000',
                      padding: '4px',
                      width: '15%',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>
                      {content.content}
                    </Text>
                  </View>
                  <View
                    style={{
                      fontFamily: 'Helvetica-Bold',
                      padding: '4px',
                      width: '20%',
                    }}
                  >
                    <Text style={{ alignSelf: 'center' }}>{content.notes}</Text>
                  </View>
                </View>
                {stop.pickUp.map((load, index) => (
                  <View
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      borderLeft: '1px solid #000',
                      borderRight: '1px solid #000',
                      borderBottom: '1px solid #000',
                    }}
                  >
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '20%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-start' }}>
                        {load.customerReference}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-start' }}>
                        {content.loading}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.pallets}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.ldmm / 1000}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.weight}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.content}
                      </Text>
                    </View>
                    <View
                      style={{
                        display: 'flex',
                        width: '20%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-start' }}></Text>
                    </View>
                  </View>
                ))}
                {stop.dropOff.map((load, index) => (
                  <View
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      borderLeft: '1px solid #000',
                      borderRight: '1px solid #000',
                      borderBottom: '1px solid #000',
                    }}
                  >
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '20%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-start' }}>
                        {load.customerReference}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-start' }}>
                        {content.unloading}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.pallets}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.ldmm / 1000}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.weight}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderRight: '1px solid #000',
                        display: 'flex',
                        width: '15%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-end' }}>
                        {load.content}
                      </Text>
                    </View>
                    <View
                      style={{
                        display: 'flex',
                        width: '20%',
                        padding: '4px',
                      }}
                    >
                      <Text style={{ alignSelf: 'flex-start' }}></Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: '20px',
            marginBottom:
              uniqueRequirements && uniqueRequirements.length > 0
                ? '0px'
                : '20px',
            width: '100%',
          }}
        >
          <Text
            style={{
              fontFamily: 'Helvetica-Bold',
              fontSize: '12px',
              paddingLeft: '5px',
              width: '30%',
            }}
          >
            {content.freightPrice}
          </Text>
          <Text
            style={{
              flex: 1,
              textAlign: 'left',
              width: '70%',
            }}
          >
            {totalCost && `${formatCurrency(totalCost)} € zzgl. MwSt.`}
          </Text>
        </View>

        {uniqueRequirements && uniqueRequirements.length > 0 && (
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: '10px',
              marginBottom: '20px',
              width: '100%',
            }}
          >
            <Text
              style={{
                paddingLeft: '5px',
                width: '30%',
              }}
            >
              {content.equipment}
            </Text>
            <Text
              style={{
                flex: 1,
                textAlign: 'left',
                width: '70%',
              }}
            >
              {uniqueRequirements.join(', ')}
            </Text>
          </View>
        )}
        <Text
          style={{
            flex: 1,
            fontFamily: 'Times-Italic',
            color: 'gray',
          }}
        >
          {content.legal}
        </Text>
        <View
          fixed
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: '10px',
            width: '100%',
            borderTop: '2px solid #000',
            padding: '10px 5px 10px 5px',
            position: 'absolute',
            bottom: '0',
            left: '35px',
            backgroundColor: '#E5E4E2',
          }}
        >
          <View>
            <View>
              <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: '12px' }}>
                {shipper.name}
              </Text>
              <Text>{shipper.address?.street}</Text>
              <Text>{`${shipper.address?.country}-${shipper.address?.zipCode} ${shipper.address?.city}`}</Text>
            </View>
          </View>
          <View>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <Text style={{ fontFamily: 'Helvetica-Bold' }}>{`E-Mail: `}</Text>
              <Text>{shipper.contact?.email}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <Text style={{ fontFamily: 'Helvetica-Bold' }}>{`VAT Id: `}</Text>
              <Text>{shipper.taxId ?? ''}</Text>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignSelf: 'flex-end',
              }}
            >
              <Text
                render={({ pageNumber, totalPages }) =>
                  `Seite ${pageNumber} / ${totalPages}`
                }
                fixed
              />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
